import { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Breadcrumbs, Paper, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Typography } from '@mui/material';

import { Container, MainContainer } from '../../../../components';
import { Menu } from '../Menu/Menu';
import { useToaster } from '../../../../providers/ToasterProvider';
import { ProjectContext } from '../../../../providers/projectProvider';
import { getUsbApprovalRequests } from '../../../../services/usbApprovalRequestService';
import { UsbApprovalDecisionModal } from './UsbApprovalDecisionModal';
import { describeUsbDevice, formatDateTime, seatOf } from './usbApprovalFormat';

import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import ACTION_VIEW from '../../../../assets/images/action-view.svg';

const STATUS_LABELS = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' };

const pendingColumns = [
    { id: 'srNo', label: 'Sr No', minWidth: 70 },
    { id: 'venueName', label: 'Venue', minWidth: 160 },
    { id: 'seat', label: 'Lab / Seat', minWidth: 140 },
    { id: 'macAddress', label: 'MAC Address', minWidth: 160 },
    { id: 'devices', label: 'USB Devices', minWidth: 200 },
    { id: 'reason', label: 'Reason', minWidth: 200 },
    { id: 'requestedAt', label: 'Requested At', minWidth: 170 },
    { id: 'actions', label: 'Action', minWidth: 90 },
];

// Without the MAC address (shown in the request's details), so the decision columns fit.
const decidedColumns = [
    ...pendingColumns.slice(0, -1).filter((column) => column.id !== 'macAddress'),
    { id: 'status', label: 'Status', minWidth: 110 },
    { id: 'decidedBy', label: 'Decided By', minWidth: 150 },
    { id: 'decidedAt', label: 'Decided At', minWidth: 170 },
    { id: 'actions', label: 'Action', minWidth: 90 },
];

// Requests from the venue kiosks to let in USB devices their security agent blocked. The
// project admin approves or rejects each one; the venue applies the decision within a minute.
export const UsbApprovalRequests = () => {

    const { projectDetails } = useContext(ProjectContext);
    const { addNotification } = useToaster();
    const [tab, setTab] = useState('pending');
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);

    const fetchRequests = useCallback(async (status) => {
        setLoading(true);
        try {
            const res = await getUsbApprovalRequests(status);
            if (res?.code === 'OK') {
                setRequests(res.data?.list ?? []);
            }
        } catch (error) {
            setRequests([]);
            console.log('There is an error while fetching USB approval requests', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchRequests(tab); }, [tab, fetchRequests]);

    const handleDecisionComplete = (requestId, status) => {
        setRequests((list) => list.filter((request) => request.id !== requestId));
        setSelectedRequest(null);
        addNotification(`Request ${status} successfully.`, 'success');
    }

    const columns = tab === 'pending' ? pendingColumns : decidedColumns;

    const renderTableCell = (columnId, row, rowIndex) => {
        switch (columnId) {
            case 'srNo':
                return rowIndex + 1;
            case 'seat':
                return seatOf(row);
            case 'devices':
                return row.devices.map(describeUsbDevice).join(', ');
            case 'requestedAt':
                return formatDateTime(row.requestedAt);
            case 'status':
                return STATUS_LABELS[row.status] ?? row.status;
            case 'decidedBy':
                return row.decidedByName ?? row.decidedBy ?? '-';
            case 'decidedAt':
                return formatDateTime(row.decidedAt);
            case 'actions':
                return <Box component='span' sx={{ cursor: 'pointer' }} onClick={() => setSelectedRequest(row)}>
                    <img src={ACTION_VIEW} alt='' />
                </Box>
            default:
                return row?.[columnId] ?? '-';
        }
    }

    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography color="text.primary" sx={{ fontWeight: 400, textTransform: 'capitalize' }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>
                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>USB Approval Requests</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>
            <Container sx={{
                display: 'flex',
                flexDirection: 'column'
            }}>
                <h1 className='title'>USB Approval Requests</h1>

                <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ mb: 2 }}>
                    <Tab value='pending' label='Pending' />
                    <Tab value='decided' label='Decided' />
                </Tabs>

                <Paper sx={{
                    width: '100%',
                    overflow: 'hidden',
                    bgcolor: 'background.base',
                    maxHeight: 'calc(100vh - 355px)',
                    maxWidth: 'calc(100vw - 140px)',
                    borderRadius: 1,
                    mb: 4
                }}>
                    <TableContainer sx={{
                        px: 1,
                        pb: 1,
                        maxHeight: 'inherit',
                        maxWidth: 'inherit',
                        border: '1px solid',
                        borderColor: 'primary.main',
                        borderRadius: 1
                    }}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            sx={{ color: 'primary.main', px: 1.5 }}
                                            key={column.id}
                                            align='center'
                                            style={{ minWidth: column.minWidth }}
                                        >
                                            {column.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!requests.length
                                    ? <TableRow sx={{ mx: 'auto', p: 2, color: 'grey.500' }}>
                                        <TableCell colSpan={columns.length} sx={{ border: 'none', textAlign: 'center' }}>
                                            {loading ? 'Loading…' : 'No records available'}
                                        </TableCell>
                                    </TableRow>
                                    : requests.map((row, rowIndex) => (
                                        <TableRow hover={false} key={row.id}>
                                            {columns.map((column) => (
                                                <TableCell key={column.id} align='center' sx={{ px: 1.5, py: 1 }}>
                                                    {renderTableCell(column.id, row, rowIndex)}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <UsbApprovalDecisionModal
                    request={selectedRequest}
                    onClose={() => setSelectedRequest(null)}
                    onDecisionComplete={handleDecisionComplete}
                />
            </Container>
        </MainContainer>
    );
}
